import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { Observable, take } from "rxjs";

import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { HaModalService } from "@ha/ui/common";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { WheelDatetimePickerModalComponent } from "../../../shared/components/wheel-datetime-picker/wheel-datetime-picker-modal.component";
import { PermitCreateActions } from "../../states/permit-create.actions";
import { PermitCreateSelectors } from "../../states/permit-create.selectors";

@UntilDestroy()
@Component({
    selector: "pmt-valid-time",
    templateUrl: "./valid-time.component.html",
    styleUrls: ["./valid-time.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidTimeComponent implements OnInit, OnChanges {
    @Input()
    public default: number;
    @Input()
    public comment: string;

    @Select(PermitCreateSelectors.isPremium)
    public isPremium$: Observable<boolean>;

    @Select(PermitCreateSelectors.validDateTime)
    public validDateTime$: Observable<string>;

    public validDateTime: string;
    public canProceed = true;
    public startPermitAction = PermitCreateActions.StartPermit;
    public form: UntypedFormGroup;

    constructor(
        private modalService: HaModalService,
        private fb: UntypedFormBuilder,
        private store: Store,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.default && !changes.default.firstChange) {
            const newDateTime = new Date(new Date().setHours(new Date().getHours() + this.default)).toJSON();
            this.store.dispatch(new PermitCreateActions.SetValidDateTime(newDateTime));
        }
    }

    public ngOnInit(): void {
        this.form = this.fb.group({
            validDateTime: [{ value: "", disabled: true }, Validators.required],
        });

        // Set initial validDateTime based on default hours
        const initialDateTime = new Date(new Date().setHours(new Date().getHours() + this.default)).toJSON();
        this.store.dispatch(new PermitCreateActions.SetValidDateTime(initialDateTime));

        this.validDateTime$.pipe(untilDestroyed(this)).subscribe((dateTime) => {
            this.validDateTime = dateTime;
            this.form.get("validDateTime").setValue(dateTime);
            this.cdr.detectChanges();
        });

        this.actions$.pipe(ofActionSuccessful(PermitCreateActions.StartPermit)).pipe(untilDestroyed(this), take(1))
            .subscribe(() => {
                this.store.dispatch(new PermitStepActions.GetCurrentStep());
            });

        this.actions$.pipe(ofActionSuccessful(PermitCreateActions.SetValidDateTime)).pipe(untilDestroyed(this))
            .subscribe(() => {
                const validDateTime = this.store.selectSnapshot(PermitCreateSelectors.validDateTime);
                const validDate = new Date(validDateTime);
                const now = new Date();
                const diffInHours = Math.floor((validDate.getTime() - now.getTime()) / (1000 * 60 * 60));

                this.canProceed = this.store.selectSnapshot(PermitCreateSelectors.isPremium)
                    ? diffInHours <= this.default
                    : true;

                this.cdr.detectChanges();
            });
    }

    showUpdateTimeModal() {
        const isPremium = this.store.selectSnapshot(PermitCreateSelectors.isPremium);
        this.modalService.open(WheelDatetimePickerModalComponent, true, {
            default: this.validDateTime,
            maxValue: isPremium ? this.default : 168,
        });
    }

    public start(): void {
        const isDemo = this.store.selectSnapshot(PermitCreateSelectors.isDemo);

        if (isDemo) {
            this.store.dispatch(new PermitCreateActions.EndDemoOrNotFlammableHotWork());
        } else {
            this.store.dispatch(new PermitCreateActions.StartPermit(this.validDateTime));
        }
    }

    public goBack(): void {
        this.store.dispatch(new PermitStepActions.GoBackwards());
    }
}
